// src/components/AiDashboard/AiMyNutritionStatusResult.jsx
import React, { useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./AiMyNutritionStatusResult.css";
import { buildNutrientBaselineSentence } from "./AiMyNutritionStatus/nutrientCopyHelpers";

/**
 * PRODUCTION GUARDRAILS & UTILITIES
 * (Adapted from SubscriptionInvoicePdfDownLoad.jsx)
 */

// Unique ID Generator
const mkId = (prefix) =>
  `${prefix || "req"}_${(
    crypto?.randomUUID?.() || Math.random().toString(36).slice(2)
  ).replace(/-/g, "")}`;

// Content Hashing for Idempotency
const computeChangeHash = (data) => {
  if (!data) return "hash_empty";

  const payloadToHash = JSON.stringify({
    title: data.title || "",
    dateRange: data.dateRange || "",
    macroSummary: data.macroSummary || "",
    lowCount: (data.lowNutrients || []).length,
    highCount: (data.highNutrients || []).length,
    onTrackCount: (data.onTrackNutrients || []).length
  });

  let hashAccumulator = 0x811c9dc5;
  for (let index = 0; index < payloadToHash.length; index += 1) {
    hashAccumulator ^= payloadToHash.charCodeAt(index);
  }
  return `legal_${(hashAccumulator >>> 0).toString(36)}`;
};

/** Simple In-Memory Idempotency Store with TTL */
class IdempotencyStore {
  constructor({ ttlMs = 30 * 60 * 1000 } = {}) {
    this.ttlMs = ttlMs;
    this.map = new Map();
  }

  tryStart(key, changeHash) {
    const now = Date.now();
    const entry = this.map.get(key);

    if (entry && entry.expiresAt > now && entry.status === "in_progress") {
      return false;
    }

    this.map.set(key, {
      status: "in_progress",
      changeHash,
      expiresAt: now + this.ttlMs
    });

    return true;
  }

  markComplete(key, changeHash) {
    const now = Date.now();
    this.map.set(key, {
      status: "complete",
      changeHash,
      expiresAt: now + this.ttlMs
    });
  }
}

/** Event Publisher (Stubbed for client-side usage matching pattern) */
class EventPublisher {
  constructor({ service }) {
    this.service = service;
    this.dlq = [];
  }

  publish(envelope, { ingress, egress }) {
    // eslint-disable-next-line no-console
    console.info(`[${this.service}] publish`, {
      ingress,
      egress,
      idempotencyKey: envelope.idempotencyKey,
      traceId: envelope.traceId,
      hopCount: envelope.hopCount,
      visited: envelope.visited
    });
    return { ok: true };
  }
}

/** Exponential Backoff Executor */
async function withBackoff(fn, { attempts = 3, baseMs = 300 } = {}) {
  let lastErr;
  for (let attemptIndex = 0; attemptIndex < attempts; attemptIndex += 1) {
    try {
      return await fn(attemptIndex + 1);
    } catch (err) {
      lastErr = err;
      if (attemptIndex === attempts - 1) {
        break;
      }
      const backoffDelay = baseMs * Math.pow(2, attemptIndex);
      await new Promise((resolve) => setTimeout(resolve, backoffDelay));
    }
  }
  throw lastErr;
}

/**
 * Measure safe breakpoints from DOM elements marked with data-pdf-block="keep-together".
 * Returns an array of Y-positions (in CSS pixels from root top) where page breaks are safe.
 */
function measureSafeBreakpoints(rootElement) {
  if (!rootElement) return [];

  const rootRect = rootElement.getBoundingClientRect();
  const blocks = rootElement.querySelectorAll(
    '[data-pdf-block="keep-together"]'
  );

  const breakpoints = [];
  blocks.forEach((block) => {
    const blockRect = block.getBoundingClientRect();
    const yFromRootTop = blockRect.bottom - rootRect.top;
    breakpoints.push(yFromRootTop);
  });

  return breakpoints.sort((a, b) => a - b);
}

/**
 * PDF Pagination Helper with Content-Aware Page Breaks
 * - Fits the captured canvas into at most `maxPages` A4 pages.
 * - Scales content down uniformly if needed so the full height
 *   fits within maxPages * usableHeight.
 * - Snaps page breaks to safe boundaries (after keep-together blocks).
 */
async function canvasToPdf(pdf, canvas, {
  marginMm = 0,
  maxPages = 10,
  safeBreakpoints = [],
  rootElementHeight = 0
} = {}) {
  if (!canvas || !canvas.width || !canvas.height) {
    return;
  }

  const pageWidthMm = pdf.internal.pageSize.getWidth();
  const pageHeightMm = pdf.internal.pageSize.getHeight();

  const usableWidthMm = pageWidthMm - marginMm * 2;
  const usableHeightMm = pageHeightMm - marginMm * 2;

  let ratioMmPerPixel = usableWidthMm / canvas.width;

  const safeMaxPages = maxPages && maxPages > 0 ? maxPages : 2;
  const maxTotalHeightMm = usableHeightMm * safeMaxPages;
  let totalHeightMm = canvas.height * ratioMmPerPixel;

  if (totalHeightMm > maxTotalHeightMm) {
    ratioMmPerPixel = maxTotalHeightMm / canvas.height;
    totalHeightMm = maxTotalHeightMm;
  }

  const contentWidthMm = canvas.width * ratioMmPerPixel;
  const horizontalMarginMm = (pageWidthMm - contentWidthMm) / 2;

  const sliceHeightPixels = Math.max(
    1,
    Math.floor(usableHeightMm / ratioMmPerPixel)
  );

  const canvasBreakpoints = [];
  if (rootElementHeight > 0 && safeBreakpoints.length > 0) {
    const scaleFactor = canvas.height / rootElementHeight;
    safeBreakpoints.forEach((domY) => {
      const canvasY = Math.floor(domY * scaleFactor);
      if (canvasY > 0 && canvasY < canvas.height) {
        canvasBreakpoints.push(canvasY);
      }
    });
  }

  let verticalOffsetPixels = 0;
  let pageIndex = 0;

  while (verticalOffsetPixels < canvas.height && pageIndex < safeMaxPages) {
    const remainingPixels = canvas.height - verticalOffsetPixels;
    const isLastPage = pageIndex === safeMaxPages - 1;

    let pageEnd;
    if (isLastPage || remainingPixels <= sliceHeightPixels) {
      pageEnd = canvas.height;
    } else {
      const tentativeEnd =
        verticalOffsetPixels + Math.min(sliceHeightPixels, remainingPixels);
      pageEnd = tentativeEnd;

      if (canvasBreakpoints.length > 0) {
        for (let i = canvasBreakpoints.length - 1; i >= 0; i -= 1) {
          const breakpoint = canvasBreakpoints[i];
          if (breakpoint > verticalOffsetPixels && breakpoint <= tentativeEnd) {
            pageEnd = breakpoint;
            break;
          }
        }
      }
    }

    const currentSliceHeightPixels = pageEnd - verticalOffsetPixels;

    if (currentSliceHeightPixels <= 0) {
      break;
    }

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = currentSliceHeightPixels;

    const sliceContext = sliceCanvas.getContext("2d");
    sliceContext.drawImage(
      canvas,
      0,
      verticalOffsetPixels,
      canvas.width,
      currentSliceHeightPixels,
      0,
      0,
      sliceCanvas.width,
      sliceCanvas.height
    );

    const imageData = sliceCanvas.toDataURL("image/png");

    if (pageIndex > 0) {
      pdf.addPage();
    }

    pdf.addImage(
      imageData,
      "PNG",
      horizontalMarginMm,
      marginMm,
      contentWidthMm,
      currentSliceHeightPixels * ratioMmPerPixel
    );

    verticalOffsetPixels = pageEnd;
    pageIndex += 1;
  }
}

/**
 * MAIN COMPONENT
 * Renders a small preview button that triggers a hidden
 * A4 PDF generation using html2canvas + jsPDF.
 */
export default function AiMyNutritionStatusResult({
  data,
  userName = "Valued Member",
  brand = {
    name: "Pure Health Nexus",
    url: "https://purehealthnexus.com",
    logo: "/assets/images/logo.png"
  },
  serviceName = "nutrition_pdf_generator"
}) {
  const documentRef = useRef(null);
  const [isBusy, setIsBusy] = useState(false);

  const idempotencyStore = useMemo(() => new IdempotencyStore(), []);
  const eventPublisher = useMemo(
    () => new EventPublisher({ service: serviceName }),
    [serviceName]
  );

  const envelope = useMemo(() => {
    const requestId = mkId("req");
    const changeHash = computeChangeHash(data);
    const idempotencyKey = changeHash;

    return {
      source: "ui",
      service: serviceName,
      requestId,
      traceId: requestId,
      idempotencyKey,
      changeHash,
      visited: ["ui"],
      hopCount: 1,
      publishedAt: new Date().toISOString()
    };
  }, [data, serviceName]);

  const filename = `Nutrition_Status_${envelope.requestId}.pdf`;

  const handleDownload = async () => {
    if (!data) return;
    if (isBusy) return;

    const canStart = idempotencyStore.tryStart(
      envelope.idempotencyKey,
      envelope.changeHash
    );
    if (!canStart) {
      console.warn("Generation already in progress for this content.");
      return;
    }

    setIsBusy(true);
    eventPublisher.publish(envelope, {
      ingress: "ui:click/preview",
      egress: "worker:generate/pdf"
    });

    try {
      await withBackoff(async () => {
        const rootNode = documentRef.current;
        if (!rootNode) {
          throw new Error("Document DOM not ready.");
        }

        const rootRect = rootNode.getBoundingClientRect();
        const safeBreakpoints = measureSafeBreakpoints(rootNode);
        const rootElementHeight = rootRect.height;

        const canvas = await html2canvas(rootNode, {
          scale: 2.0,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          windowWidth: rootNode.scrollWidth,
          windowHeight: rootNode.scrollHeight
        });

        const pdf = new jsPDF("p", "mm", "a4");
        pdf.setProperties({
          title: data.title || "Nutrition Status",
          subject: `Nutrition Analysis • ${data.dateRange || "Current Period"}`,
          author: brand.name,
          keywords: `nutrition,analysis,${brand.name}`,
          creator: serviceName
        });

        await canvasToPdf(pdf, canvas, {
          marginMm: 0,
          maxPages: 10,
          safeBreakpoints,
          rootElementHeight
        });

        pdf.save(filename);
      });

      idempotencyStore.markComplete(
        envelope.idempotencyKey,
        envelope.changeHash
      );
    } catch (error) {
      console.error("Nutrition PDF Generation Failed:", error);
      eventPublisher.dlq.push({
        envelope,
        reason: error.message
      });
      // eslint-disable-next-line no-alert
      alert("Unable to generate the document at this time.");
    } finally {
      setIsBusy(false);
    }
  };

  if (!data) return null;

  // Category → CSS modifier mapping so colors stay centralized.
  const summaryCategoryClassByKey = {
    low: "summary-card--low",
    high: "summary-card--high",
    track: "summary-card--track"
  };

  const renderNutrientCard = (title, items, categoryKey) => {
    if (!items || items.length === 0) return null;

    const modifierClassName =
      summaryCategoryClassByKey[categoryKey] || "summary-card--generic";

    return (
      <div className={`summary-card ${modifierClassName}`} key={categoryKey}>
        <div className="summary-card-header">
          <span className="summary-card-pill" />
          <p className="summary-card-label">{title}</p>
        </div>
        <ul className="summary-list">
          {items.map((item, index) => {
            const nutrientName = item.displayName || item.name;
            const baselineSentence = buildNutrientBaselineSentence(item);
            const recommendationSentence =
              item.recommendation || item.summary || "";
            const summaryText = recommendationSentence
              ? `${baselineSentence} ${recommendationSentence}`
              : baselineSentence;

            return (
              <li
                key={`${categoryKey}-${index}`}
                className="summary-item"
                data-pdf-block="keep-together"
              >
                <div className="summary-item-heading">
                  <span className="summary-item-name">
                    {nutrientName}
                  </span>
                  {typeof item.percentOfTarget === "number" && (
                    <span className="summary-item-percent">
                      (~{Math.round(item.percentOfTarget)}% of target)
                    </span>
                  )}
                </div>
                {summaryText && (
                  <p className="summary-item-text">
                    {summaryText}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <>
      {/* VISIBLE UI: Small Clickable Preview */}
      <div
        className="legal-preview-container"
        onClick={handleDownload}
        role="button"
        aria-label="Download Nutrition Status PDF"
        title="Download Nutrition Status (PDF)"
      >
        {isBusy && (
          <div className="preview-spinner-overlay">
            <div className="preview-spinner" />
          </div>
        )}
        <div className="preview-line title" />
        <div className="preview-line" />
        <div className="preview-line" />
        <div className="preview-line short" />
        <div className="preview-line" />
        <div className="preview-line" />
        <div className="preview-line short" />
      </div>

      {/* HIDDEN RENDER TARGET: A4 PDF CONTENT */}
      <div className="phn-invoice-hidden-wrapper" aria-hidden="true">
        <div ref={documentRef} className="phn-invoice a4">
          {/* Accent Bar */}
          <div className="accent-bar" aria-hidden="true" />

          {/* Header: Brand + Meta */}
          <header className="inv-header">
            <div className="brand">
              <img
                src={brand.logo || "/assets/images/logo.png"}
                className="brand-logo"
                alt={`${brand.name} logo`}
              />
              <div className="brand-text">
                <h1 className="brand-name">{brand.name}</h1>
                <p className="brand-url">{brand.url}</p>
              </div>
            </div>
            <div className="inv-meta">
              <p className="inv-title">
                {data.title || "Nutrition Status"}
              </p>
              <p className="inv-number">
                # {envelope.requestId.slice(4, 16).toUpperCase()}
              </p>
              <p className="inv-date">
                Issued:{" "}
                {new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </p>
            </div>
          </header>

          {/* Metadata Section */}
          <section className="inv-billto">
            <div className="col">
              <p className="k">Prepared for</p>
              <p className="v">{userName}</p>
            </div>
            <div className="col">
              <p className="k">Analysis Period</p>
              <p className="v">
                {data.dateRange || "Current period"}
              </p>
            </div>
            <div className="col">
              <p className="k">Generated</p>
              <p className="v">
                {new Date().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
              </p>
            </div>
          </section>

          {/* Executive Summary */}
          {data.macroSummary && (
            <section className="inv-plan">
              <div className="plan-line">
                <div className="plan-left">
                  <p className="plan-name">Summary</p>
                  <p className="plan-summary-text">
                    {data.macroSummary}
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* Nutrient Analysis (single stacked column, modern cards) */}
          {(data.lowNutrients?.length > 0 ||
            data.highNutrients?.length > 0 ||
            data.onTrackNutrients?.length > 0) && (
            <section className="summary-linear">
              <div className="summary-column">
                {renderNutrientCard(
                  "Nutrients Below Target",
                  data.lowNutrients,
                  "low"
                )}
                {renderNutrientCard(
                  "Nutrients Above Target",
                  data.highNutrients,
                  "high"
                )}
                {renderNutrientCard(
                  "Nutrients On Track",
                  data.onTrackNutrients,
                  "track"
                )}
              </div>
            </section>
          )}

          {/* Disclaimer */}
          <section className="inv-notes">
            <p className="disclaimer-text">
              <strong>Disclaimer &amp; Limitations:</strong>{" "}
              {data.disclaimer ||
                "This analysis is based on self-reported data. It is provided for informational purposes only and does not constitute medical advice, diagnosis, or treatment. Consult a qualified healthcare professional before undertaking significant dietary changes."}
            </p>
          </section>

          {/* Footer */}
          <footer className="inv-footer">
            <p className="fine">
              © {new Date().getFullYear()} {brand.name}
            </p>
            <p className="fine-right">
              Generated from your logged nutrition data • Not medical
              advice
            </p>
          </footer>
        </div>
      </div>
    </>
  );
}
